import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Card,
  CardContent,
  Alert,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios, { AxiosResponse } from 'axios';

interface SheetProps {
  pharmacyNum: string;
}

interface SheetData {
  id: string;
  name: string;
  webViewLink: string;
}

interface GoogleDriveResponse {
  files: SheetData[];
  nextPageToken?: string;
}

interface GoogleTokenResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
}

const GoogleSheetViewer: React.FC<SheetProps> = ({ pharmacyNum }) => {
  const [sheetData, setSheetData] = useState<SheetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [folderId, setFolderId] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const GOOGLE_API_CONFIG = {
    clientId: '781945341153-s6b2jrk76knme2hvjqveka00qui1lu1j.apps.googleusercontent.com',
    clientSecret: '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',
    refreshToken: '1//043-eWXl92KIMCgYIARAAGAQSNwF-L9Ir7JcfcVt1kcaxYGKR3whplZ8qMsAt2WNNNebrzfS-1tUswRs9bTw1UfrbaJrNL2YQTo0'
  };

  const getAccessToken = async (): Promise<string> => {
    try {
      const response: AxiosResponse<GoogleTokenResponse> = await axios.post(
        'https://oauth2.googleapis.com/token',
        {
          client_id: GOOGLE_API_CONFIG.clientId,
          client_secret: GOOGLE_API_CONFIG.clientSecret,
          refresh_token: GOOGLE_API_CONFIG.refreshToken,
          grant_type: 'refresh_token',
        }
      );
      return response.data.access_token;
    } catch (err) {
      throw new Error('Failed to get access token');
    }
  };

  const getAllFiles = async (accessToken: string, currentFolderId: string): Promise<SheetData[]> => {
    let allFiles: SheetData[] = [];
    let pageToken: string | undefined = undefined;

    do {
      try {
        const response: AxiosResponse<GoogleDriveResponse> = await axios.get(
          'https://www.googleapis.com/drive/v3/files',
          {
            params: {
              q: `'${currentFolderId}' in parents and mimeType='application/vnd.google-apps.spreadsheet'`,
              fields: 'nextPageToken, files(id, name, webViewLink)',
              pageSize: 1000,
              pageToken: pageToken,
            },
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        allFiles = [...allFiles, ...response.data.files];
        pageToken = response.data.nextPageToken;
      } catch (err) {
        console.error('Error fetching page:', err);
        throw err;
      }
    } while (pageToken);

    return allFiles;
  };

  const getEmbedUrl = (webViewLink: string): string => {
    return webViewLink.replace('/view', '/embed');
  };

  // функции для работы с куками
  const setCookie = (name: string, value: string, expires: Date) => {
    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; secure; samesite=strict`;
  };

  const getCookie = (name: string): string | null => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  const getEndOfYearDate = (): Date => {
    const now = new Date();
    const endOfYear = new Date(now.getFullYear(), 11, 31, 23, 59, 59); // 31 декабря 23:59:59
    return endOfYear;
  };

  const clearFolderIdCache = () => {
    document.cookie = 'timesheet_folder_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    console.log('Folder ID cache cleared');
  };

  // проверка URL параметра для принудительной очистки кэша
  const checkForCacheClear = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('clearCache') === 'timesheet') {
      clearFolderIdCache();
      // удалить параметр из URL после очистки
      urlParams.delete('clearCache');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  };

  const getFolderId = async (): Promise<string> => {
    const cookieName = 'timesheet_folder_id';

    // сначала проверка куки
    const cachedFolderId = getCookie(cookieName);
    if (cachedFolderId) {
      console.log('Using cached folder ID from cookie');
      return cachedFolderId;
    }

    // Если в куках нет - запрос к API
    try {
      console.log('Fetching folder ID from API...');
      const response = await axios.get('https://tbl.zerdegroup.kz/api/v1/timesheet/folderID');

      if (response.data.success && response.data.folderId) {
        const folderId = response.data.folderId;

        // сохранить в куки до конца текущего года
        const expires = getEndOfYearDate();
        setCookie(cookieName, folderId, expires);

        return folderId;
      } else {
        throw new Error('Folder ID not found');
      }
    } catch (err) {
      console.error('Error fetching folder ID:', err);
      throw new Error('Failed to get folder ID from server');
    }
  };

  const findPharmacySheet = async () => {
    try {
      setLoading(true);
      setError(null);

      checkForCacheClear();

      const currentFolderId = await getFolderId();
      setFolderId(currentFolderId);

      const accessToken = await getAccessToken();
      const allFiles = await getAllFiles(accessToken, currentFolderId);

      const possibleSheetNames = [
        `Табель аптека №${pharmacyNum}_`,
        `Табель Аптека №${pharmacyNum}_`,
        `Табель аптека №${pharmacyNum}`,
        `Табель Аптека №${pharmacyNum}`
      ];

      const sheet = allFiles.find(
        (file: SheetData) => possibleSheetNames.includes(file.name.trim())
      );

      if (sheet) {
        setSheetData(sheet);
      } else {
        setError(`Таблица для аптеки №${pharmacyNum} не найдена`);
      }
    } catch (err) {
      setError('Ошибка при получении данных таблицы');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    findPharmacySheet();
  }, [pharmacyNum]);

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, p: 0, '&:last-child': { pb: 0 } }}>
          <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
        <Button
          variant="outlined"
          color="primary"
          onClick={findPharmacySheet}
          sx={{ mt: 2 }}
        >
          Попробовать снова
        </Button>
      </Box>
    );
  }

  if (!sheetData) {
    return null;
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, p: 0, '&:last-child': { pb: 0 }, height: '700px', position: 'relative' }}>
        <iframe
          src={getEmbedUrl(sheetData.webViewLink)}
          width="100%"
          height="100%"
          frameBorder="0"
          title={sheetData.name}
          style={{
            display: 'block',
            border: 'none',
          }}
        />
        <Tooltip title="Открыть в новом окне">
          <IconButton
            href={sheetData.webViewLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'white',
              position: 'absolute',
              bottom: 1,
              right: 1,
              backgroundColor: 'rgba(26, 115, 232, 0.9)',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: 'rgba(26, 115, 232, 1)',
              }
            }}
          >
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
      </CardContent>
    </Card>
  );
};

export default GoogleSheetViewer;
